/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */
import { clamp, createDisposer, logDebug, logInfo, logWarn, noop } from '@amazon/vinyl-util'
import { combineData, data, type ObservableValue } from '@amazon/vinyl-observable'
import type { AppDependencies } from '@/appDependencies'
import {
    globalData,
    type PlaylistController,
    type PlaylistEntry,
    type PlayParams as CloudQueuePlayParams,
} from '@amzn/amp-playback'
import type { BrowseItem, PlayParams as BrowsePlayParams } from '@/blackbolt/types'
import { getAuth } from '@/auth/authRef'
import {
    isKnownStubRejection as isKnownStubRejectionWith,
    type StubRejectionRules,
} from '@/blackbolt/stubRejection'
import { ctx } from '@/util/context'
import {
    clearInteractionStart,
    markInteractionStart,
    type PlaybackMeasureToken,
} from '@/perf/playbackDelayMarks'

const { target, errorHandler } = ctx('playback')

/**
 * The slice of `AppDependencies` we need to drive playback from a card
 * click. Kept narrow so tests can pass a minimal fake without also
 * fabricating a full `AppDependencies` container.
 */
export type PlaybackDeps = Pick<
    AppDependencies,
    'cloudQueueController' | 'playlistController' | 'playerPromise' | 'streamingSettings'
>

let deps: PlaybackDeps | null = null

/**
 * Per-item playback state a card play button renders from — the web
 * analog of iOS's `PlaybackStateStore.buttonState(for: itemId)`.
 * `idle` and `paused` both show the play glyph; they differ in what a
 * tap does (create a fresh queue vs. resume the parked player).
 */
export type ItemPlaybackState = 'idle' | 'playing' | 'paused' | 'loading'

/**
 * Mirror of the shared player's paused / buffering flags, refreshed
 * from the same events the transport's PlayButton listens to. Kept at
 * module scope so every card button shares ONE set of player
 * subscriptions instead of racing `playerPromise` per mount.
 */
const playerState$ = globalData(() =>
    data<{ readonly paused: boolean; readonly loading: boolean }>({
        paused: true,
        loading: false,
    })
)

/**
 * The playing entry, mirrored from the bound playlist controller. Route
 * surfaces that live outside the transport module (e.g. the chat
 * NanoPlayer) read this instead of importing the controller directly —
 * `transport/` already depends on `blackbolt/`, so a reverse
 * `blackbolt → transport` edge would be a new import cycle. Cleared to
 * null when deps are unbound so a surface can't strand a stale track.
 */
const currentEntry$ = globalData(() => data<PlaylistEntry | null>(null))

/**
 * `playParams.id` of the item whose `createCloudQueue` call is in
 * flight — the tap-to-first-audio window the queue observables can't
 * see yet. Cleared on resolve AND reject so a failed create doesn't
 * strand a card on the spinner.
 */
const pendingPlayId$ = globalData(() => data<string | null>(null))

let disposePlayerStateSub: (() => void) | null = null

/**
 * Install the app's dependency container so `play(item)` can reach the
 * real transport. Called from `AppView`'s `onConnect` so cards on every
 * route (feed on `/`, chat cards on `/chat`) can drive playback; the
 * slot is cleared from the returned disposer on shell tear-down.
 */
export function bindPlaybackState(next: PlaybackDeps | null): void {
    deps = next
    disposePlayerStateSub?.()
    disposePlayerStateSub = null
    if (!next) {
        currentEntry$.value = null
        return
    }
    let disposed = false
    const { add, dispose } = createDisposer()
    add(next.playlistController.current.onData((entry) => (currentEntry$.value = entry)))
    next.playerPromise
        .then((player) => {
            if (disposed) return
            const refresh = () => {
                playerState$.value = {
                    paused: player.paused$.value || player.ended$.value,
                    loading: player.loading$.value,
                }
            }
            add(player.paused$.onData(refresh))
            add(player.ended$.onData(refresh))
            add(player.loading$.onData(refresh))
        })
        .catch((err: Error) => logInfo(target, err.message))
    disposePlayerStateSub = () => {
        disposed = true
        dispose()
    }
}

/**
 * Subscribe to the live play / pause / loading state for one card
 * item. Fires immediately with the current state and on every change.
 * An item counts as "current" when the active cloud queue was created
 * from its `playParams.id` (hero buttons on album / playlist cards) or
 * when the playing entry's entity uri matches `item.uri` (track rows).
 * Returns a disposer; no-ops to `idle` when deps aren't installed or
 * the item isn't playable.
 */
export function subscribeItemPlaybackState(
    item: BrowseItem,
    onState: (state: ItemPlaybackState) => void
): () => void {
    const controller = deps?.playlistController
    const itemId = item.playParams?.id ?? null
    if (!controller || itemId == null) {
        onState('idle')
        return noop
    }
    const state$ = combineData({
        queueParams: controller.playParams,
        current: controller.current,
        flags: playerState$,
        pending: pendingPlayId$,
    }).map(({ queueParams, current, flags, pending }): ItemPlaybackState => {
        if (pending === itemId) return 'loading'
        const isCurrent =
            queueParams?.id === itemId || (item.uri != null && current?.entity.uri === item.uri)
        if (!isCurrent) return 'idle'
        if (flags.loading) return 'loading'
        return flags.paused ? 'paused' : 'playing'
    })
    return state$.onData(onState)
}

/**
 * Pause the shared player. Used by card play buttons when their item
 * is the one playing — mirrors iOS `pauseTapped` → `playbackService.pause()`.
 */
export function pausePlayback(): void {
    deps?.playerPromise
        .then((player) => player.pause())
        .catch((err: Error) => logInfo(target, err.message))
}

/**
 * Resume the parked player without re-creating the queue — used when
 * the tapped item is already the active source but paused. Re-running
 * `play(item)` here would restart the queue from the top.
 */
export function resumePlayback(): void {
    deps?.playerPromise
        .then((player) => player.play())
        .catch((err: Error) => logInfo(target, err.message))
}

/**
 * Set the shared player's output volume, mirroring what a VolumeControl slider
 * drag does: write `volume$` (0..1) and derive `muted$` from it (a level of 0
 * mutes, any level above unmutes). Reaching the player through `playerPromise`
 * keeps this on the same seam pause/resume use, so the transport slider tracks
 * an agent-driven change with no extra wiring. No-op when no player is bound.
 *
 * @param level output volume in the 0..1 range; callers clamp before this point.
 */
export function setPlaybackVolume(level: number): void {
    const vol = clamp(level, 0, 1)
    deps?.playerPromise
        .then((player) => {
            player.volume$.value = vol
            player.muted$.value = vol <= 0
        })
        .catch((err: Error) => logInfo(target, err.message))
}

/**
 * The playing entry (or null when nothing is queued). Route surfaces
 * outside the transport module render now-playing info off this.
 */
export const nowPlaying$: ObservableValue<PlaylistEntry | null> = currentEntry$

/** True while the shared player is paused or has run to end. */
export const nowPlayingPaused$: ObservableValue<boolean> = playerState$.map((s) => s.paused)

/** True while the shared player is buffering the current entry. */
export const nowPlayingLoading$: ObservableValue<boolean> = playerState$.map((s) => s.loading)

/** Resume when paused, pause when playing — the play/pause affordance. */
export function togglePlayback(): void {
    if (playerState$.value.paused) resumePlayback()
    else pausePlayback()
}

// Re-exported so /chat's now-playing surfaces open the expanded player
// through the same façade they already read playback state from. See the
// module note on nowPlaying$ for the layering rationale.
export { openFullPlayer } from '@/playback/fullPlayerState'

/**
 * Test / debug helper — returns the currently installed deps (or null).
 */
export function getPlaybackDeps(): PlaybackDeps | null {
    return deps
}

/**
 * Firefly-side error codes / message fragments that indicate "the id
 * you sent isn't a real playable entity" — expected outcomes for
 * dev-stub fixtures and stale card ids.
 */
const STUB_REJECTION_RULES: StubRejectionRules = {
    codes: new Set([
        'NOT_FOUND',
        'INVALID_INPUT',
        'BAD_REQUEST',
        'RECOMMENDATION_NOT_FOUND',
        'ENTITY_NOT_FOUND',
    ]),
    messageFragments: [
        'not found',
        'unknown entity',
        'invalid playparams',
        'invalid album',
        'invalid track',
        'no data available',
    ],
}

/**
 * Swallow a `createCloudQueue` rejection that only means "this id isn't a
 * real playable entity" — a live card whose upstream entity was deleted,
 * or a `?feedStub=1` fixture id Firefly can't resolve. Nothing the user
 * can do about a stale recommendation, so log at warn (keeps the
 * occurrence in telemetry) rather than surfacing the loud "request
 * failed, exhausted N tries" error. Any other rejection goes to
 * errorHandler.
 */
function handleCreateQueueRejection(
    item: BrowseItem,
    err: unknown,
    measureToken?: PlaybackMeasureToken
): void {
    // The play failed before setQueue — drop the pending interaction so it can't
    // dangle and be adopted by a later machine setQueue (party) as a bogus start.
    // Token-guarded so a newer play's window survives our reject.
    clearInteractionStart(measureToken)
    if (isKnownStubRejection(err)) {
        logWarn(target, 'createCloudQueue rejected id (stub / unknown), swallowing', {
            itemId: item.id,
            error: (err as Error).message,
        })
        return
    }
    errorHandler(err as Error)
}

/**
 * Clear the in-flight play id once its `createCloudQueue` settles (resolve
 * OR reject) — from here the player's own readyState drives the loading
 * presentation, and a failed create must not strand the card on the
 * spinner. Guarded so a newer tap that already moved `pendingPlayId$` on
 * isn't cleared by an older settle.
 */
function clearPendingPlayId(id: string | null): void {
    if (pendingPlayId$.value === id) pendingPlayId$.value = null
}

/**
 * Handle a card play-button tap. Hands off to the shared
 * cloudQueueController. No "Playing …" toast fires; the tap is
 * intentionally silent and the visible feedback is the Now Playing
 * surface updating once the transport starts. If deps haven't been
 * installed yet, or the item has no `playParams.id` (thin concert /
 * catalog-gap), we skip the transport call.
 *
 * `overrideParams`, when passed, seeds the queue from THOSE play params
 * instead of the item's own — `playInContext` uses it to queue a track
 * from its list container (the recommendation edge's playParams) so
 * playback continues through the list.
 */
export function play(item: BrowseItem, overrideParams?: BrowsePlayParams | null): void {
    logDebug(target, 'play', item)

    const rawPlayParams = overrideParams ?? item.playParams
    // `normalizePlayParams` returns null exactly when the id is missing, so this
    // one guard covers both the thin-item case and the normalized shape.
    const playParams = rawPlayParams ? normalizePlayParams(rawPlayParams) : null
    if (!deps || playParams == null) {
        return
    }
    if (!getAuth().isSignedIn()) {
        // Anonymous sessions can't create cloud queues; mirror
        // RecommendationRenderer's guard rather than letting
        // createCloudQueue throw an UnauthorizedError toast.
        return
    }

    // Video items must NOT go through DMLS/cloud queue — route through
    // PRS via the video queue instead, matching GH's getVideoInfo gate.
    if (item.type === 'video' || item.video === true) {
        playVideo(item, deps).catch(errorHandler)
        return
    }

    const controller = deps.cloudQueueController
    // The user (or agent) expressed a NEW-track play intent here — open the perf
    // journey window ahead of the lazy import + createQueue, so both fall inside
    // it. The resolved-track lane is derived later (createQueue result / seed).
    const measureToken = markInteractionStart()
    // Publish the in-flight window so the tapped card's play button can
    // show a spinner before the queue observables reflect the new source.
    pendingPlayId$.value = playParams.id
    // Lazy — the queue-manipulation service (createQueue + shuffle / loop /
    // add / report mutations) is only needed once the user actually starts
    // playback, so it loads off the app-shell chunk on first play.
    import('@amzn/amp-playback/cloudQueueService')
        .then(({ createCloudQueue }) => createCloudQueue({ playParams }))
        .then((queueResult) => {
            controller.setQueue({ playParams, queueResult })
        })
        .catch((err: unknown) => {
            handleCreateQueueRejection(item, err, measureToken)
        })
        .finally(() => clearPendingPlayId(playParams.id))
}

/**
 * Routes a video BrowseItem through the PRS playback path. Resolves
 * the video Track via PRS and sets a single-track video queue on the
 * playlist controller so the transport plays the video manifest.
 */
async function playVideo(item: BrowseItem, deps: PlaybackDeps): Promise<void> {
    const trackId = item.playParams?.id ?? item.id
    const { setVideoQueue } = await import('@/video/videoQueue')
    const { resolveVideoTrack } = await import('@/video/videoPlayback')
    const image = item.imageUrl
        ? { url: item.imageUrl, width: null, height: null, imageType: null, colorPalette: null }
        : null
    await setVideoQueue(deps, {
        id: trackId,
        title: item.title,
        artistName: item.subtitle ?? '',
        durationSec: 0,
        image,
        trackLoadOptions: async () => {
            const resolved = await resolveVideoTrack(trackId, item.title)
            if (!resolved) throw new Error(`Could not resolve video track ${trackId}`)
            return resolved.trackLoadOptions
        },
        entity: { __typename: 'Track', id: trackId, playParams: item.playParams ?? null } as never,
    })
}

/**
 * Adapt `BrowseItem.playParams` to the Firefly `PlayParams` shape
 * that `createCloudQueue` (and downstream `setQueue`) expects.
 *
 * Firefly emits either `containerIds` (plural — the canonical
 * mutation-input shape) OR the singular `containerId` on some rows.
 * Prefer the plural list when present; otherwise lift the singular
 * slot into a one-element array so the mutation still carries the
 * container context.
 *
 * Returns `null` when `raw.id` is missing so callers can no-op
 * without a follow-up narrowing check.
 */
export function normalizePlayParams(raw: BrowsePlayParams): CloudQueuePlayParams | null {
    if (raw.id == null) return null
    const wireIds = raw.containerIds?.filter((c): c is string => c != null) ?? null
    const containerIds =
        wireIds && wireIds.length > 0 ? wireIds : raw.containerId != null ? [raw.containerId] : []
    const selections = raw.selections?.filter((s): s is string => s != null) ?? []
    return { id: raw.id, containerIds, selections }
}

/**
 * Play `item` as part of the list it's shown in, so playback continues to
 * the next track.
 *
 * RewindApp threads the LIST container through the recommendation edge's
 * playParams (`playEntityEdge`): a track's `listPlayParams.containerIds`
 * reference the collection, so a single `createCloudQueue` seeds a queue
 * that flows to the next track — no sibling bookkeeping. Prefer that path.
 *
 * When the row carries no list container (chat-hydrated / agent-authored
 * rows whose wire ships no edge playParams) fall back to seeding a
 * single-track queue and appending the visible `siblings` in card order
 * (after-then-before the clicked position, wrapping around), or to a bare
 * `play(item)` when the item isn't a track or has no playable siblings.
 */
export function playInContext(item: BrowseItem, siblings: readonly BrowseItem[]): void {
    if (item.type === 'track' && item.listPlayParams) {
        const listParams = normalizePlayParams(item.listPlayParams)
        if (listParams && (listParams.containerIds?.length ?? 0) > 0) {
            play(item, item.listPlayParams)
            return
        }
    }

    const trackSiblings = siblings.filter(
        (s) => s !== item && s.type === 'track' && s.playParams?.id != null
    )
    if (item.type !== 'track' || trackSiblings.length === 0) {
        play(item)
        return
    }

    logDebug(target, 'playInContext', { item: item.id, siblingCount: trackSiblings.length })

    const rawPlayParams = item.playParams
    if (!deps || rawPlayParams?.id == null) return
    if (!getAuth().isSignedIn()) return

    // Strip containerIds so the gateway creates a single-track queue
    // rather than queueing the track's parent album/playlist.
    const playParams: CloudQueuePlayParams = {
        id: rawPlayParams.id,
        containerIds: [],
        selections: [],
    }

    const controller = deps.cloudQueueController
    const { playlistController } = deps
    // A NEW-track play intent — open the perf journey window ahead of createQueue.
    // The resolved-track lane is derived later (createQueue result / seed entry).
    const measureToken = markInteractionStart()
    pendingPlayId$.value = playParams.id

    // Reorder siblings: items after the clicked position, then items
    // before — gives wrap-around (C,D,A,B when clicking C in [A,B,C,D]).
    const clickIdx = siblings.indexOf(item)
    const after = siblings
        .slice(clickIdx + 1)
        .filter((s) => s.type === 'track' && s.playParams?.id != null)
    const before = siblings
        .slice(0, clickIdx)
        .filter((s) => s.type === 'track' && s.playParams?.id != null)
    const ordered = [...after, ...before]

    import('@amzn/amp-playback/cloudQueueService')
        .then(({ createCloudQueue }) =>
            createCloudQueue({ playParams, playbackContinuationMode: 'AUTOPLAY_OFF' })
        )
        .then((queueResult) => {
            controller.setQueue({ playParams, queueResult })
            const queueId = queueResult.createQueue?.queue?.id
            if (queueId && ordered.length > 0) {
                // Background append; appendSiblings logs its own per-item
                // failures, so swallow the import-level rejection here.
                appendSiblings(queueId, ordered, playlistController).catch(() => {})
            }
        })
        .catch((err: unknown) => handleCreateQueueRejection(item, err, measureToken))
        .finally(() => clearPendingPlayId(playParams.id))
}

/**
 * Sequentially append tracks to an existing cloud queue. Best-effort —
 * individual failures are logged but do not abort the remaining adds.
 *
 * When the controller's up-next is empty at the start of the batch (the
 * play_tracks / card-context seam seeds a single-track queue, so its
 * prefetched `next` resolves to null), a tail append can't reach the
 * controller's cached lookup on its own. Invalidate once after the batch
 * so `next` re-walks the now-longer queue and the transport's Next button
 * enables — without waiting for the current track to end. Guarded on the
 * empty→non-empty transition so appending to a queue that already has an
 * up-next (immediate next unchanged by a tail add) doesn't re-fetch.
 */
async function appendSiblings(
    queueId: string,
    items: readonly BrowseItem[],
    controller: PlaylistController
): Promise<void> {
    const wasEmpty = controller.next.value == null
    const { addEntityToCloudQueue } = await import('@amzn/amp-playback/cloudQueueService')
    let appended = false
    for (const sibling of items) {
        const entityId = sibling.playParams?.id
        if (!entityId) continue
        try {
            await addEntityToCloudQueue(queueId, entityId)
            appended = true
        } catch (e) {
            logWarn(target, 'appendSiblings: failed to add', {
                entityId,
                error: (e as Error).message,
            })
        }
    }
    if (wasEmpty && appended) controller.invalidateNext()
}

/** Classify a createCloudQueue rejection. See {@link isKnownStubRejectionWith}. */
export function isKnownStubRejection(err: unknown): boolean {
    return isKnownStubRejectionWith(err, STUB_REJECTION_RULES)
}
